/**
 * Main Application Component
 *
 * Root component that sets up routing and Redux store.
 * Includes feature flag integration for dynamic route access control.
 *
 * @author Sabya
 * @date September 30, 2025
 */

import { Route, Routes } from "react-router-dom";
import React, { Suspense } from "react";

// ============================================================
// REDUX STORE INTEGRATION
// Provides global state management for feature flags
// ============================================================
import { Provider } from "react-redux";
import store from "./redux/store";

import GlobalLayout from "./layouts/global.layout";
import ResultLayout from "./layouts/result.layout";
import Protected from "./components/Protected";

// ============================================================
// FEATURE GUARD COMPONENT
// Protects routes based on feature flags from Redux store
// ============================================================
import FeatureGuard from "./components/FeatureGuard";


const Login = React.lazy(() => import("./pages/login.page"));
const NotFound = React.lazy(() => import("./pages/not-found.page"));
const LandingPage = React.lazy(() => import("./pages/landing.page"));
const CreateProjectPage = React.lazy(() => import("./pages/create-project.page"));
const AdditionalDetailsPage = React.lazy(() => import("./pages/additional-details.page"));
const AddPersonaPage = React.lazy(() => import("./pages/add-personas.page"));
const AddConceptPage = React.lazy(() => import("./pages/add-concepts.page"));
const ConceptReport = React.lazy(() => import("./pages/ConceptReport"));
const ConceptAnalysisPage = React.lazy(() => import("./pages/concept-analysis.page"));
const PurchaseDriversPage = React.lazy(() => import("./pages/purchase-drivers.page"));
const RadarPage = React.lazy(() => import("./pages/radar.page"));
const SuccessFactorsPage = React.lazy(() => import("./pages/success-factors.page"));
const PersonaAnalysisPage = React.lazy(() => import("./pages/persona-analysis.page"));
const ComparingConceptsPage = React.lazy(() => import("./pages/comparing-concepts.page"));
const ScoresPage = React.lazy(() => import("./pages/scores.page"));
const IdealConceptPage = React.lazy(() => import("./pages/ideal-concept.page"));
// const CompetitorInsightsPage = React.lazy(() => import("./pages/competitor-insights.page"));
// const FeatureAppealPage = React.lazy(() => import("./pages/feature-appeal.page"));

/**
 * App Component
 *
 * Main application component that wraps all routes with Redux Provider
 * and implements feature-gated routing for dynamic access control.
 *
 * @author Sabya
 * @date September 30, 2025
 */
export default function App() {
  return (
    // ============================================================
    // Redux Provider: Makes store available to all components
    // ============================================================
    <Provider store={store}>
      <Suspense fallback={
        <div className='w-full h-screen flex justify-center items-center'>
          <div className="h-12 w-12 border-[4px] border-[#4F46E5] border-t-transparent rounded-full animate-spin"></div>
        </div>}>
        <Routes>
          <Route element={<Protected />}>
            {/* ========================================
                PROJECT SETUP ROUTES (No Feature Flags)
                ======================================== */}
            <Route element={<GlobalLayout />}>
              <Route path="/" index element={<LandingPage />} />
              <Route path="/:projectId">
                <Route path="create-project" element={<CreateProjectPage />} />
                <Route path="add-details" element={<AdditionalDetailsPage />} />
                <Route path="personas" element={<AddPersonaPage />} />
                <Route path="concept" element={<AddConceptPage />} />
              </Route>
            </Route>

            {/* ========================================
                RESULTS ROUTES (With Feature Flags)
                @author Sabya | @date September 30, 2025
                ======================================== */}
            <Route path=":projectId/:runId" element={<ResultLayout />}>
              {/* Overview - Always accessible (no guard) */}
              <Route path="concept-report" element={<ConceptReport />} />

              {/* ========================================
                  FEATURE-GATED ROUTES
                  Each route wrapped with FeatureGuard
                  Redirects to overview if disabled
                  ======================================== */}

              {/* Concept Analysis */}
              <Route
                path="concept-analysis"
                element={
                  <FeatureGuard feature="conceptAnalysis">
                    <ConceptAnalysisPage />
                  </FeatureGuard>
                }
              />

              {/* Persona Analysis */}
              <Route
                path="persona-analysis"
                element={
                  <FeatureGuard feature="personaAnalysis">
                    <PersonaAnalysisPage />
                  </FeatureGuard>
                }
              />

              {/* Success Factors */}
              <Route
                path="success-factor"
                element={
                  <FeatureGuard feature="successFactors">
                    <SuccessFactorsPage />
                  </FeatureGuard>
                }
              />

              {/* Comparing Concepts */}
              <Route
                path="compare-concept"
                element={
                  <FeatureGuard feature="comparingConcepts">
                    <ComparingConceptsPage />
                  </FeatureGuard>
                }
              />

              {/* Scores */}
              <Route
                path="scores"
                element={
                  <FeatureGuard feature="scores">
                    <ScoresPage />
                  </FeatureGuard>
                }
              />

              {/* Ideal Concept */}
              <Route
                path="ideal-concept"
                element={
                  <FeatureGuard feature="idealConcept">
                    <IdealConceptPage />
                  </FeatureGuard>
                }
              />

              {/* Purchase Drivers */}
              <Route
                path="purchase-drivers"
                element={
                  <FeatureGuard feature="purchaseDrivers">
                    <PurchaseDriversPage />
                  </FeatureGuard>
                }
              />

              {/* Radar Chart */}
              <Route
                path="radar"
                element={
                  <FeatureGuard feature="radar">
                    <RadarPage />
                  </FeatureGuard>
                }
              />
              {/* <Route path="competitor-insights" element={<CompetitorInsightsPage />} /> */}
            </Route>
          </Route>

        <Route path="/login" element={<Login />} />
        <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Provider>
  );
}
